
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Flame } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

interface GuideItem {
  id: number;
  title: string;
  fileSize: string;
  date: string;
  credits: number;
  isPremiere: boolean;
  url?: string;
  category?: string;
}

const ResourcesGuides = () => {
  const [guides, setGuides] = useState<GuideItem[]>([]);
  const { userCredits, setUserCredits } = useAuth();

  // Load guides from local storage
  useEffect(() => {
    // First check for guides from admin upload
    const adminFiles = localStorage.getItem('adminFiles');
    let guidesFromAdmin: GuideItem[] = [];
    
    if (adminFiles) {
      const parsedFiles = JSON.parse(adminFiles);
      guidesFromAdmin = parsedFiles
        .filter((file: any) => file.category === 'guides')
        .map((file: any) => ({
          id: file.id,
          title: file.name,
          fileSize: file.size,
          date: file.uploadDate,
          credits: 3, // Default credit cost
          isPremiere: false,
          url: file.url,
          category: file.category
        }));
    }
    
    // Then add demo guides if we don't have enough
    const demoGuides = [
      { id: 1001, title: "Οδηγός Λογιστικών Εγγραφών", fileSize: "1.5 MB", date: "15/06/2023", credits: 3, isPremiere: false },
      { id: 1002, title: "Αυτοματοποίηση Εργασιών", fileSize: "2.3 MB", date: "20/07/2023", credits: 4, isPremiere: true },
      { id: 1003, title: "Φορολογικές Δηλώσεις Βήμα-Βήμα", fileSize: "3.1 MB", date: "05/08/2023", credits: 5, isPremiere: false },
      { id: 1004, title: "Μισθοδοσία και Ένσημα", fileSize: "1.8 MB", date: "12/09/2023", credits: 3, isPremiere: false },
      { id: 1005, title: "Ισολογισμός - Πρακτικός Οδηγός", fileSize: "2.7 MB", date: "25/10/2023", credits: 4, isPremiere: false },
      { id: 1006, title: "Αποθέματα και Απογραφή", fileSize: "1.9 MB", date: "08/11/2023", credits: 3, isPremiere: false }
    ];
    
    // Combine admin guides with demo guides
    setGuides([...guidesFromAdmin, ...demoGuides]);
  }, []);

  const handleDownload = (guideId: number, guideCredits: number) => {
    if (userCredits < guideCredits) {
      toast({
        title: "Ανεπαρκή Credits",
        description: "Δεν έχετε αρκετά credits για να κατεβάσετε αυτόν τον οδηγό.",
        variant: "destructive"
      });
      return;
    }

    // Deduct credits
    setUserCredits(userCredits - guideCredits);

    // Find the guide
    const guide = guides.find(g => g.id === guideId);
    
    toast({
      title: "Λήψη Οδηγού",
      description: `Ξεκινάει η λήψη του οδηγού «${guide?.title}». Αφαιρέθηκαν ${guideCredits} credits.`
    });
    
    // If guide has a URL (from admin upload), open it
    if (guide?.url) {
      window.open(guide.url, '_blank');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Goto Οδηγίες</h1>
          <p className="text-muted-foreground mt-2">
            Αναλυτικοί οδηγοί βήμα-προς-βήμα για τις λειτουργίες του λογισμικού
          </p>
        </div>

        <div className="flex justify-between items-center bg-muted/50 p-4 rounded-lg mb-4">
          <span className="font-medium">Διαθέσιμα Credits:</span>
          <span className="font-bold text-primary">{userCredits} Credits</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {guides.map((guide) => (
            <Card key={guide.id} className="relative overflow-hidden">
              {guide.isPremiere && (
                <Badge variant="destructive" className="absolute top-2 right-2 flex items-center">
                  <Flame className="h-3 w-3 mr-1" /> ΠΡΕΜΙΕΡΑ
                </Badge>
              )}
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{guide.title}</CardTitle>
                <CardDescription>Ημερομηνία: {guide.date}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <FileText className="h-10 w-10 text-primary" />
                  <div>
                    <h3 className="font-medium">Οδηγίες χρήσης λογισμικού</h3>
                    <p className="text-sm text-muted-foreground">PDF, {guide.fileSize}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-primary flex items-center">
                    <span className="inline-block px-2 py-1 bg-primary/10 rounded-md text-primary font-medium mr-1">
                      {guide.credits}
                    </span> 
                    Credits
                  </span>
                  <Button 
                    size="sm"
                    onClick={() => handleDownload(guide.id, guide.credits)}
                    disabled={userCredits < guide.credits}
                  >
                    {userCredits >= guide.credits ? 'Λήψη' : 'Ανεπαρκή Credits'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </MainLayout>
  );
};

export default ResourcesGuides;
